import { Observable, of } from 'rxjs';

import {
  IGatewayTokenResponse,
  IPaymentConfirmationResponse,
  IPaymentConfirmPayload,
  IPaymentApiMock,
  IPaymentsVersions,
  ICancelSubscriptionResponse,
  IUpdatePaymentMethodPayload,
  IStartVtbPaymentPayload,
} from '../private/payment-api/payment-api.interface';
import { AuthLevel, Authorize } from '../private/authorize.decorator';
import {
  AxiosInstanceType,
  SlAxiosInstanceService,
} from '../private/services/sl-axios-instance.service';
import { SlApiContext } from '../private/api-context';
import { IApiUrls, IHostsConfigs } from '../private/global.interface';
import { constructUrl } from '../private/utils/httpParamsUtils';
import { Service, Container } from '../../symphony';
import { PaymentProduct, PaymentProvider } from './subscription-api';

@Service()
export class PaymentApi {
  private subscriptionApiHost: string;

  private useMock: boolean;

  private mock: IPaymentApiMock;

  private axiosInstance: AxiosInstanceType;

  private versions: IPaymentsVersions;

  constructor() {
    this.subscriptionApiHost = (
      Container.take('global', 'envUrl') as IApiUrls
    ).subscriptionApiHost;
    this.useMock = (
      Container.take('global', 'hostsConfigs') as IHostsConfigs
    )?.paymentApi?.useMock;
    this.mock = (
      Container.take('global', 'hostsConfigs') as IHostsConfigs
    )?.paymentApi?.mock;
    this.versions = (
      Container.take('global', 'hostsConfigs') as IHostsConfigs
    )?.paymentApi?.version;
    this.axiosInstance = Container.take(
      SlApiContext,
      SlAxiosInstanceService,
    ).axios;
  }

  @Authorize(AuthLevel.authenticated)
  public getGatewayToken(): Observable<IGatewayTokenResponse> {
    if (this.useMock) return of(this.mock.gatewayToken);
    return this.axiosInstance.get(
      constructUrl(
        this.subscriptionApiHost,
        'api/subscriptions/braintree/gateway-authorization',
        this.versions?.getGatewayToken,
      ),
    );
  }

  @Authorize(AuthLevel.authenticated)
  public subscribe(
    payload: IPaymentConfirmPayload,
  ): Observable<IPaymentConfirmationResponse> {
    if (this.mock) return of(this.mock.paymentConfirmation);
    return this.axiosInstance.post(
      constructUrl(
        this.subscriptionApiHost,
        'api/subscriptions/braintree',
        this.versions?.subscribe,
      ),
      payload,
    );
  }

  @Authorize(AuthLevel.authenticated)
  public cancelSubscription(): Observable<ICancelSubscriptionResponse> {
    return this.axiosInstance.post(
      constructUrl(
        this.subscriptionApiHost,
        'api/subscriptions/cancel',
        this.versions?.cancelSubscription,
      ),
      null,
    );
  }

  @Authorize(AuthLevel.authenticated)
  public updatePaymentMethod(payload: IUpdatePaymentMethodPayload): Observable<any> {
    return this.axiosInstance.put(
      constructUrl(
        this.subscriptionApiHost,
        'api/subscriptions/update/paymentmethod',
        this.versions?.updatePaymentMethod,
      ),
      payload,
    );
  }

  @Authorize(AuthLevel.authenticated)
  public startVtbPayment(payload: IStartVtbPaymentPayload): Observable<any> {
    return this.axiosInstance.post(
      constructUrl(
        this.subscriptionApiHost,
        'api/subscriptions/vtb/payment',
        this.versions?.vtb,
      ),
      payload,
    );
  }

  @Authorize(AuthLevel.authenticated)
  public getProductInfo(provider: PaymentProvider, productId: string): Observable<PaymentProduct[]> {
    return this.axiosInstance.get(
      constructUrl(this.subscriptionApiHost, `api/products/${provider}?productIds=${productId}`),
    );
  }
}

export * from '../private/payment-api/payment-api.interface';
